import type { Folder } from '../types'

/** Must match FolderPath.MaxDepth in lib/src/Services/FolderPath.cs. */
export const MAX_FOLDER_DEPTH = 25

export interface FolderNode {
  folder: Folder
  children: FolderNode[]
  /** 1-based, matching the server's path segment count. */
  depth: number
}

/**
 * Folds the flat `folders/list-all` response into a tree. The API orders by path, so parents
 * always precede their children and one pass suffices; the ordering is not relied on for
 * correctness though — an orphan (parent outside the workspace) is surfaced at the root rather
 * than dropped.
 */
export function buildFolderTree(folders: Folder[]): FolderNode[] {
  const nodes = new Map<string, FolderNode>()
  for (const folder of folders) {
    nodes.set(folder.folderId, { folder, children: [], depth: 0 })
  }

  const roots: FolderNode[] = []
  for (const node of nodes.values()) {
    const parent = node.folder.parentFolderId ? nodes.get(node.folder.parentFolderId) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  const assignDepth = (list: FolderNode[], depth: number) => {
    for (const node of list) {
      node.depth = depth
      assignDepth(node.children, depth + 1)
    }
  }
  assignDepth(roots, 1)

  const sortByName = (list: FolderNode[]) => {
    list.sort((a, b) => a.folder.name.localeCompare(b.folder.name))
    for (const node of list) sortByName(node.children)
  }
  sortByName(roots)

  return roots
}

/** Root-to-target chain, inclusive, for the breadcrumb. */
export function ancestorChain(folders: Folder[], folderId: string | null): Folder[] {
  if (!folderId) return []
  const byId = new Map(folders.map(f => [f.folderId, f]))
  const chain: Folder[] = []
  let current = byId.get(folderId)
  while (current) {
    chain.unshift(current)
    current = current.parentFolderId ? byId.get(current.parentFolderId) : undefined
  }
  return chain
}

/**
 * Whether `candidate` is `ancestor` itself or sits beneath it — the client-side cycle guard, so
 * an illegal drop target never highlights. Mirrors FolderPath.IsSelfOrDescendant: both paths
 * carry a trailing slash, so a plain prefix test cannot straddle a segment boundary.
 */
export function isSelfOrDescendant(candidatePath: string, ancestorPath: string): boolean {
  if (!candidatePath || !ancestorPath) return false
  const prefix = ancestorPath.endsWith('/') ? ancestorPath : `${ancestorPath}/`
  const candidate = candidatePath.endsWith('/') ? candidatePath : `${candidatePath}/`
  return candidate.startsWith(prefix)
}

export function pathDepth(path: string | null | undefined): number {
  if (!path) return 0
  return path.split('/').filter(Boolean).length
}

/** Deepest depth anywhere in the subtree rooted at `node`, self-inclusive. */
export function subtreeMaxDepth(node: FolderNode): number {
  let max = pathDepth(node.folder.path)
  for (const child of node.children) max = Math.max(max, subtreeMaxDepth(child))
  return max
}
